import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle } from 'lucide-react';

const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, type = 'danger', confirmText, t, expectedName }) => {
  const [inputValue, setInputValue] = React.useState('');
  
  if (!isOpen || typeof document === 'undefined') return null;

  const isConfirmed = expectedName ? inputValue === expectedName : true;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="absolute inset-0 bg-slate-950/90 backdrop-blur-xl cursor-pointer" />
          <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} className="bg-slate-900 border border-white/10 p-10 rounded-[40px] shadow-3xl max-w-sm w-full relative z-10 text-center">
            <div className={`w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 ${type === 'danger' ? 'bg-rose-500/10' : 'bg-primary/10'}`}>
              <AlertCircle size={40} className={type === 'danger' ? 'text-rose-500' : 'text-primary'} />
            </div>
            <h3 className="text-xl font-black text-white mb-2">{title}</h3>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">{message}</p>
            
            {expectedName && (
              <div className="mb-8 text-left">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-2 px-1">
                  {t('confirmType', { name: expectedName })}
                </p>
                <input 
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onPaste={(e) => e.preventDefault()}
                  placeholder={expectedName}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white text-sm focus:border-rose-500 transition-all focus:outline-none placeholder:opacity-20"
                />
              </div>
            )}

            <div className="flex gap-3">
              <button 
                onClick={() => { onClose(); setInputValue(''); }} 
                className="flex-1 px-6 py-4 rounded-2xl font-bold text-slate-400 hover:bg-white/5 transition-all text-sm"
              >
                {t('cancel')}
              </button>
              <button 
                disabled={!isConfirmed}
                onClick={() => { onConfirm(); onClose(); setInputValue(''); }} 
                className={`flex-1 px-6 py-4 rounded-2xl font-bold text-white shadow-lg transition-all text-sm ${
                  !isConfirmed ? 'bg-slate-800 text-slate-500 cursor-not-allowed grayscale' :
                  type === 'danger' ? 'bg-rose-500 hover:bg-rose-600 shadow-rose-500/20' : 'bg-primary hover:bg-blue-600 shadow-primary/20'
                }`}
              >
                {confirmText || t('confirm')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ConfirmationModal;
